import { useId } from "react";

interface SparklineProps {
  points: number[];
  color?: string;
  width?: number;
  height?: number;
}

/**
 * Tiny smoothed area sparkline. `points` are raw values (any scale); the path
 * is normalized to the series' own min/max. Fed by the real daily trend series
 * from GET /admin/stats.
 */
export function Sparkline({ points, color = "#1F5A9E", width = 200, height = 32 }: SparklineProps) {
  const gradientId = useId();
  if (!points.length) return null;

  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const stepX = width / (points.length - 1);
  const ys = points.map((p) => height - ((p - min) / range) * (height - 4) - 2);

  let d = `M0 ${ys[0]}`;
  for (let i = 1; i < points.length; i++) {
    const x = i * stepX;
    const y = ys[i];
    const px = (i - 1) * stepX;
    const py = ys[i - 1];
    const cx = (px + x) / 2;
    d += ` Q ${cx} ${py} ${cx} ${(py + y) / 2} T ${x} ${y}`;
  }
  const area = `${d} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
